"use client";

import classNames from "classnames";
import { useRouter } from "next/navigation";
import { useContext } from "react";

import { FooterCopyright } from "@/components/Footer/FooterCopyright";
import { Button as ButtonLib } from "@npm_leadtech/qr-ui-components";
import { Link } from "@/components/Link";
import { Typography } from "@/components/Typography";

import { paths } from "@/utils/paths";
import { getSiteInfo } from "@/sites";

import { FooterProps } from "./types";

import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";
import { Logo } from "../Logo";

import { getSiteDomain } from "@/utils/site";
import { DomainName } from "@/sites/domains";

import { Trustpilot } from "../Trustpilot/Trustpilot";
import "./styles.scss";

export const Footer = ({ footerCTAHref, noMb, customClass }: FooterProps) => {
  const { siteBlackLogoFooter, siteName } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!) || {};
  const siteDomain = getSiteDomain();
  const isQRCreatorDomain = siteDomain === DomainName.QRCreator;

  const router = useRouter();
  const { setSource } = useContext(AmplitudeContext);

  const classnames = classNames("footer", {
    footerNoMB: noMb,
    [`${customClass}`]: customClass,
  });

  const onCreateQrCodeClick = () => {
    setSource("link_footer_create");
    router.push(footerCTAHref);
  };

  const onPricesClick = () => {
    setSource("footer_plan_&_pricing");
    router.push(footerCTAHref);
  };

  const getTrustpilotComponent = () => {
    if (isQRCreatorDomain) {
      return (
        <div className="trustpilot">
          <Trustpilot />
        </div>
      );
    }

    return null;
  };

  return (
    <footer
      className={classnames}
      data-testid="footer"
    >
      <div className="footer-grid">
        <div className="footer-col logo-col">
          <Logo
            src={siteBlackLogoFooter}
            siteName={siteName}
            dataQa="footer-logo-link"
            dataTestId="footer-logo"
          />
        </div>

        <div className="footer-col cta-col">
          <div className="cta-section">
            <Typography
              Tag="p"
              size="l"
              classname="cta-section-text"
            >
              Make your own QR codes today.
            </Typography>

            <ButtonLib
              id="footer-qr-editor-button"
              variant="secondary"
              label="Create QR code"
              dataQA="footer-qr-editor-button"
              onClick={onCreateQrCodeClick}
            />
          </div>

          {getTrustpilotComponent()}
        </div>

        <div className="nav-columns">
          <div className="footer-col company-col">
            <h4 className="footer-links-title">Company</h4>
            <ul className="footer-links">
              <li>
                <Link
                  href={paths.whoWeAre()}
                  dataQA="footer-who-we-are-button"
                >
                  Who we are
                </Link>
              </li>
              <li>
                <Link
                  href={paths.termsAndConditions()}
                  dataQA="footer-terms-and-conditions-button"
                >
                  Terms and Conditions
                </Link>
              </li>
              <li>
                <Link
                  href={paths.termsOfUse()}
                  dataQA="footer-terms-of-use-button"
                >
                  Terms of Use
                </Link>
              </li>
              <li>
                <Link
                  href={paths.privacyPolicy()}
                  dataQA="footer-privacy-policy-button"
                >
                  Privacy policy
                </Link>
              </li>
              <li>
                <Link
                  href={paths.cookiesPolicy()}
                  dataQA="footer-cookies-policy-button"
                >
                  Cookies policy
                </Link>
              </li>
              <li>
                <Link
                  href={paths.pricing()}
                  dataQA="footer-prices-button"
                  onClick={onPricesClick}
                >
                  Prices
                </Link>
              </li>
            </ul>
          </div>

          <div className="footer-col help-col">
            <h4 className="footer-links-title">Need help?</h4>
            <ul className="footer-links">
              <li>
                <Link
                  href={paths.contactUs()}
                  dataQA="footer-contact-us-button"
                >
                  Contact us
                </Link>
              </li>
              <li>
                <Link
                  href={paths.faq()}
                  dataQA="footer-faq-button"
                >
                  FAQ
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <FooterCopyright />
    </footer>
  );
};
